import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../App';
import useAuthorityProfile from '../hooks/useAuthorityProfile';
import AuthorityOnboardingGuide from './AuthorityOnboardingGuide';

/**
 * Aug 2026 — Route guard for `/admin/authority-onboarding`.
 *
 * The Printable Onboarding Guide is written FOR verified Town Halls / Police
 * (and admins hand it out). The old route required `role === 'admin'`, which
 * meant a signed-in authority (e.g. Arboleas Town Hall) got kicked to the
 * Admin Login page — the exact bug reported by the operator.
 *
 * Access rules:
 *   • Signed-in admin / sysadmin  → open the guide directly.
 *   • Signed-in verified authority
 *     (`user.officialSafetySource === 'town_hall' | 'police'`) → open
 *     the guide directly. Detected via `useAuthorityProfile()` which is the
 *     canonical hook already used by the Profile card, Mini-Dashboard and
 *     Welcome toast.
 *   • Not signed in OR signed in as an ordinary resident → redirect to
 *     `/admin/login` (per operator spec — a resident should never stumble
 *     onto the authority-only doc).
 *
 * While `useAuthorityProfile` is still loading (it makes one `/user/profile`
 * call), we render a lightweight spinner. Without this the component would
 * briefly redirect the authority to `/admin/login`, then flip back once the
 * profile loads — a jarring UX and worse than the bug we're fixing.
 */
const AuthorityOnboardingRoute = () => {
  const { user } = useAuth();
  const authority = useAuthorityProfile();

  // Not signed in → straight to admin login.
  if (!user?.token) {
    return <Navigate to="/admin/login" replace />;
  }

  // Admin / sysadmin can always view (that's the audience that hands it out).
  if (user.role === 'admin' || user.role === 'sysadmin') {
    return <AuthorityOnboardingGuide />;
  }

  // Wait for the authority-status resolution before deciding — no flicker.
  if (authority.loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center text-slate-500 text-sm"
        data-testid="authority-onboarding-loading"
      >
        Loading onboarding guide…
      </div>
    );
  }

  if (authority.isOfficial) {
    return <AuthorityOnboardingGuide />;
  }

  // Signed-in ordinary user → not authorised for this document.
  return <Navigate to="/admin/login" replace />;
};

export default AuthorityOnboardingRoute;
